/**
 * Maps breach names to their password reset URLs
 */

type BreachLinkMap = {
  [key: string]: string
}

const BREACH_RESET_LINKS: BreachLinkMap = {
  // Social Media
  'LinkedIn': 'https://www.linkedin.com/checkpoint/rp/request-password-reset',
  'LinkedInScrape': 'https://www.linkedin.com/checkpoint/rp/request-password-reset',
  'MySpace': 'https://myspace.com/forgotpassword',
  'Twitter': 'https://twitter.com/account/begin_password_reset',
  'Twitter200M': 'https://twitter.com/account/begin_password_reset',
  'Facebook': 'https://www.facebook.com/login/identify/',
  'Instagram': 'https://www.instagram.com/accounts/password/reset/',

  // Email & Communication
  'Adobe': 'https://account.adobe.com/',
  'Disqus': 'https://disqus.com/forgot/',

  // E-commerce
  'Canva': 'https://www.canva.com/login/',
  'StockX': 'https://stockx.com/login',
  'Chegg': 'https://www.chegg.com/auth',
  'Poshmark': 'https://poshmark.com/user/password/reset',
  'HauteLook': 'https://www.nordstromrack.com/signin', // HauteLook merged with Nordstrom Rack
  'LiveAuctioneers': 'https://www.liveauctioneers.com/signin',
  'AdvanceAutoParts': 'https://shop.advanceautoparts.com/signin',
  'NeimanMarcus': 'https://www.neimanmarcus.com/account/signin.jsp',
  'HomeChef': 'https://www.homechef.com/login',
  'Scentbird': 'https://www.scentbird.com/login',

  // Design & Creative
  '500px': 'https://500px.com/p/password/reset',
  'Houzz': 'https://www.houzz.com/forgotPassword',

  // Financial
  'Betterment': 'https://wwws.betterment.com/login',

  // Fitness
  'Jefit': 'https://www.jefit.com/login',

  // Entertainment
  'DatPiff': 'https://www.datpiff.com/login',

  // Streaming
  'Nitro': 'https://discord.com/reset', // Discord password reset (Nitro is Discord premium)

  // Transportation
  'ParkMobile': 'https://www.parkmobile.io/',
}

/**
 * Get password reset URL for a breach
 * Returns null if no specific URL is available
 */
export function getBreachResetLink(breachName: string | null): string | null {
  if (!breachName) return null

  // Direct match
  if (BREACH_RESET_LINKS[breachName]) {
    return BREACH_RESET_LINKS[breachName]
  }

  // Check for partial matches (case insensitive)
  const normalizedName = breachName.toLowerCase()
  for (const [key, url] of Object.entries(BREACH_RESET_LINKS)) {
    if (normalizedName.includes(key.toLowerCase())) {
      return url
    }
  }

  return null
}

/**
 * Get a user-friendly label for breach types that don't have reset links
 */
export function getBreachTypeLabel(breachName: string | null): string | null {
  if (!breachName) return null

  const aggregatedBreaches = [
    'Collection1',
    'Collection2',
    'Collection3',
    'Collection4',
    'Collection5',
    'Cit0day',
    'VerificationsIO',
    'PDL',
    'RiverCityMedia',
    'BTSec',
    'ThePostMillennial',
    'TelegramCombolists',
    'NotSOCRadar',
    'NationalPublicData',
    'FrenchCitizens',
    'Giglio',
    'SynthientStealerLogThreatData',
    'SynthientCredentialStuffingThreatData',
  ]

  const defunctSites = [
    'iMesh',
    'Pemiblanc',
    'CashCrate',
    'ExploitIn',
  ]

  if (aggregatedBreaches.some(ab => breachName.includes(ab))) {
    return 'Aggregated breach (multiple sources)'
  }

  if (defunctSites.some(ds => breachName.includes(ds))) {
    return 'Defunct or untrusted site'
  }

  return null
}

/**
 * Generic password security advice URL
 */
export const GENERIC_PASSWORD_HELP = 'https://haveibeenpwned.com/Passwords'
